import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { AppLayout, PageContainer } from './layout';
import type { NavItem } from './layout';
import { Spinner } from './ui';

const ClientsPage = lazy(() => import('../pages/admin/ClientsPage'));
const ClientRecordPage = lazy(() => import('../pages/admin/ClientRecordPage'));
const SchedulePage = lazy(() => import('../pages/admin/SchedulePage'));
const ComplianceReviewPage = lazy(() => import('../pages/admin/ComplianceReviewPage'));
const APManagementPage = lazy(() => import('../pages/admin/APManagementPage'));
const OnboardAPPage = lazy(() => import('../pages/admin/OnboardAPPage'));
const ProgressNotesReviewPage = lazy(() => import('../pages/admin/ProgressNotesReviewPage'));

const adminNavItems: NavItem[] = [
  { label: 'Schedule', path: '/admin', icon: 'schedule' },
  { label: 'Clients', path: '/admin/clients', icon: 'clients' },
  { label: 'Notes', path: '/admin/notes', icon: 'notes' },
  { label: 'Compliance', path: '/admin/compliance', icon: 'compliance' },
  { label: 'Providers', path: '/admin/providers', icon: 'providers' },
];

/**
 * Admin layout shell — wraps all /admin/* routes with responsive navigation.
 * Feature content built in Phase 5+.
 */
export default function AdminShell() {
  const { user, logout } = useAuth();

  return (
    <AppLayout
      navItems={adminNavItems}
      role="admin"
      userDisplayName={user?.name ?? 'Admin'}
      onLogout={() => void logout()}
    >
      <Suspense fallback={<PageContainer title=""><Spinner size="lg" /></PageContainer>}>
        <Routes>
          <Route index element={<SchedulePage />} />
          <Route path="schedule" element={<Navigate to="/admin" replace />} />
          <Route path="clients" element={<ClientsPage />} />
          {/* Keyed on ClientID, not the list item id (34-UI-DESIGN § 1.2).
              adminNavItems is untouched: Sidebar/BottomNav set `end` only for
              /ap and /admin, so this child route still highlights "Clients". */}
          <Route path="clients/:clientId" element={<ClientRecordPage />} />
          <Route path="notes" element={<ProgressNotesReviewPage />} />
          <Route path="compliance" element={<ComplianceReviewPage />} />
          <Route path="providers" element={<APManagementPage />} />
          <Route path="onboard" element={<OnboardAPPage />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
}
